import { Box, Chip, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsPristine } from '../../../hooks/useIsPristine';
import { uniqueId } from '../../../lib/uniqueId';
import { AutomationActionType } from '../../../types/enums/AutomationActionType';
import { ActionsMenu } from '../../common/actions-menu/ActionsMenu';
import { CreateEditDialog } from '../../common/create-edit-dialog/CreateEditDialog';
import { Button, Typography, colors } from '@sweep-io/sweep-design';
import useCheckPermissionsForCurrentCrmOrg from '../../common/permissions/useCurrentCrmOrgPermissions';
import { CustomHeader } from '../../common/rule-builder/CustomHeader';
import { SweepFieldsRuleBuilder } from '../../common/sweep-condition-rule-builder/SweepConditionRuleBuilder';
import {
  MultipleRuleBuildersProvider,
  useRefForMultipleRuleBuilders,
  useValidateAllAndReturnIsValid,
} from '../../common/sweep-condition-rule-builder/MultipleRuleBuildersContext';
import { AssignmentToType } from '../../../types/enums/AssignmentToType';
import { useAssignmentGroupsApiFacade } from '../../../apis/facades/useAssignmentGroupsApiFacade';
import cloneDeep from 'lodash/cloneDeep';
import { AutomationNotifyEmail } from '../../Automations/AutomationNotifyEmail';
import { SlackNotificationAction } from '../../Automations/slack/SlackNotificationAction';
import { AutomationActionRows } from '../../Automations/AutomationActionRows';
import { SlackAddMemberAction } from '../../Automations/slack/SlackAddMemberAction';
import { useSelector } from 'react-redux';
import {
  SlackConnectionStatus,
  selectSlackIntegrationStatus,
} from '../../../reducers/integrationsReducer';
import { useIntegrations } from '../../../hooks/useIntegrations';
import { useRunOnce } from '../../common/useRunOnce';
import useConfirm from '../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { ConfirmLeaveWithoutSaveDialog } from '../../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import { ActionObjectTypesAutocomplete } from '../../Automations/ActionObjectTypesAutocomplete';
import SweepSelect from '../../common/SweepSelect';
import { useAutomationsContext } from '../../Automations/AutomationsContext';
import { telemetry } from '../../../telemetry';
import { useCrmOrgsApiFacade } from '../../../apis/facades/useCrmOrgsApiFacade';
import { AssignmentsAssignTo } from '../../../types/AssignmentsAssignTo';
import { AssignToSection } from './AssignToSection';
import { getMembershipFieldIdFromAssignTo } from './getMembershipFieldIdFromAssignTo';
import { isAutomationFieldValid } from '../../Automations/helper';
import { SweepCondition } from '../../../types/SweepCondition';
import { SweepCriterion } from '../../../types/SweepCriterion';

interface AssignmentsDialogProps {
  initialAssignmentRule?: AutomationAssignmentRule;
  onCreateEdit: (assignmentRule: AutomationAssignmentRule) => any;
  crmOrgId: string;
  objectType: string;
  onCancel: () => any;
  editMode?: boolean;
  objectTypes?: ObjectTypeName[];
}

//TODO replace with Tag from sweep/design
const CustomChip = styled(Chip)({
  '&.MuiChip-sizeSmall': {
    borderRadius: '6px',
    height: '28px',
    '.MuiChip-label': {
      fontSize: 12,
      fontWeight: 500,
      padding: '8px',
    },
  },
});

const SectionBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

type FollowUpActionSelectOptions =
  | AutomationActionType.SendEmail
  | AutomationActionType.Slack
  | 'DO_NOT_NOTIFY';

export interface FormType {
  id: string;
  name: string;
  exitCriteria: SweepCondition;
  assignedTo: Partial<AssignmentsAssignTo>;
  assignedToFallback: Partial<AssignmentsAssignTo>;
  followUpActions: {
    action: Partial<AutomationAction>;
    actionSelectOption?: FollowUpActionSelectOptions;
  }[];
}

const isValidAssignmentAssignTo = (
  assignedTo?: Partial<AssignmentsAssignTo>,
  isFallback?: boolean,
) => {
  if (isFallback && !assignedTo) {
    return true;
  }
  switch (assignedTo?.type) {
    case AssignmentToType.Field:
      return Boolean(assignedTo._name) && Boolean(assignedTo.fieldMapping?.toField?.fieldIds?.[0]);
    case AssignmentToType.Team: {
      const hasFieldMapping = Boolean(assignedTo.fieldMapping?.toField?.fieldIds?.[0]);
      // New structure: weightedGroups array
      if (assignedTo.weightedGroups && assignedTo.weightedGroups.length > 0) {
        const allGroupsHaveId = assignedTo.weightedGroups.every((g) => Boolean(g.groupId));
        return hasFieldMapping && allGroupsHaveId;
      }
      // Old structure: single id
      return Boolean(assignedTo.id && assignedTo._name && hasFieldMapping);
    }
    case AssignmentToType.User:
    case AssignmentToType.Queue:
      return Boolean(
        assignedTo.type &&
        assignedTo.id &&
        assignedTo._name &&
        assignedTo.fieldMapping?.toField?.fieldIds?.[0],
      );
    default:
      return false;
  }
};

const isActionValid = (action: Partial<AutomationAction>) => {
  if (action.actionType === AutomationActionType.SendEmail) {
    const emailAction = action as Partial<EmailAutomationAction>;
    const areEmailsValid =
      emailAction.actionParams?.toEmailRecipients &&
      emailAction.actionParams.toEmailRecipients.length > 0;
    return emailAction.actionParams?.emailTemplate && areEmailsValid;
  }
  if (action.actionType === AutomationActionType.Slack) {
    const slackAction = action as Partial<SlackAutomationAction>;
    return slackAction.actionParams?.message && slackAction.actionParams?.recipients?.length;
  }
  if (
    action.actionType === AutomationActionType.UpdateRecords ||
    action.actionType === AutomationActionType.CreateRecord
  ) {
    const fieldsAction = action as Partial<UpdateOrCreateAutomationAction>;

    if (!fieldsAction?.actionParams?._fields) {
      return false;
    }
    if (fieldsAction?.actionParams?._fields.length === 0) {
      return false;
    }
    return !fieldsAction.actionParams._fields.find(
      (field) => !isAutomationFieldValid(field, action),
    );
  }

  if (action.actionType === AutomationActionType.Assignment) {
    return (
      isValidAssignmentAssignTo(action.actionParams?.assignedTo, false) &&
      isValidAssignmentAssignTo(action.actionParams?.assignedToFallback, true)
    );
  }

  return true;
};

const isEmptySweepCriterion = (entry: SweepCriterion) => {
  return !Boolean(entry._fieldIds.length || entry.operator || entry.value);
};

const isEmptyAction = (entry: AutomationAction) => {
  return !Boolean(entry.actionType || (entry as any).actionParams);
};

const hasOnlyOneCriteriaAndItIsEmpty = (assignmentRule: AutomationAssignmentRule) => {
  if (assignmentRule.when?.criteria?.criteria) {
    if (
      assignmentRule.when.criteria.criteria.length === 1 &&
      isEmptySweepCriterion(assignmentRule.when.criteria.criteria[0])
    ) {
      return true;
    }
  }
  return false;
};

const hasOnlyOneFollowUpAndItIsEmpty = (assignmentRule: AutomationAssignmentRule) => {
  if (assignmentRule.followUpActions && assignmentRule.followUpActions[0].actions.length === 1) {
    if (isEmptyAction(assignmentRule.followUpActions[0].actions[0])) {
      return true;
    }
  }
  return false;
};

const sanitizeAssignmentRule = (assignmentRule: AutomationAssignmentRule) => {
  const cloned = cloneDeep(assignmentRule);
  if (
    hasOnlyOneCriteriaAndItIsEmpty(assignmentRule) ||
    assignmentRule.when?.criteria?.criteria.length === 0
  ) {
    delete cloned.when;
  }
  if (
    hasOnlyOneFollowUpAndItIsEmpty(assignmentRule) ||
    assignmentRule.followUpActions?.length === 0
  ) {
    delete cloned.followUpActions;
  }
  return cloned;
};

const AssignmentRuleDialog = ({
  initialAssignmentRule,
  onCreateEdit,
  crmOrgId,
  objectType,
  onCancel,
  editMode,
}: AssignmentsDialogProps) => {
  const slackStatus = useSelector(selectSlackIntegrationStatus);
  const integrations = useIntegrations();
  const [recordTypesList, setRecordTypes] = useState<RecordTypeStruct[]>();

  useRunOnce(async () => {
    await integrations.getSlackStatus();
  });

  const { permissions } = useAutomationsContext();
  const [isAllowedBtn] = useCheckPermissionsForCurrentCrmOrg([permissions.create]);

  const formVarsFromInitialConditionalAction = (): FormType => {
    const { criteria = [], criteriaLogic = '' } = initialAssignmentRule?.when?.criteria || {};

    return {
      id: initialAssignmentRule?._id || uniqueId(),
      name: initialAssignmentRule?.name || 'New assignment rule',
      exitCriteria: {
        criteria,
        criteriaLogic,
      },
      assignedTo: initialAssignmentRule?.assignedTo || {},
      assignedToFallback: initialAssignmentRule?.assignedToFallback,
      followUpActions:
        initialAssignmentRule?.followUpActions && initialAssignmentRule.followUpActions[0]
          ? initialAssignmentRule.followUpActions[0].actions.map((action) => ({
              action: action,
              actionSelectOption: (action.actionType as FollowUpActionSelectOptions) || '',
            }))
          : [],
    };
  };

  const [formVars, setFormVars] = useState<FormType>(formVarsFromInitialConditionalAction);

  const isPristine = useIsPristine(formVars);
  const { openConfirm } = useConfirm();

  const isAssignmentsFormValid = useMemo(() => {
    const isNameValid = formVars.name.length > 0;
    const isAssignToValid =
      isValidAssignmentAssignTo(formVars.assignedTo) &&
      isValidAssignmentAssignTo(formVars.assignedToFallback, true);
    const areActionsValid = formVars.followUpActions.every(({ action }) => isActionValid(action));

    return isNameValid && isAssignToValid && areActionsValid;
  }, [formVars]);

  const ruleBuilderRef = useRefForMultipleRuleBuilders();
  const validateAllAndReturnIsValid = useValidateAllAndReturnIsValid();

  const applyCurrentAssignment = useCallback(() => {
    if (formVars.followUpActions && formVars.followUpActions.length) {
      formVars.followUpActions = formVars.followUpActions.filter((el) => el.actionSelectOption);
    }
    const updatedAssignmentRule: AutomationAssignmentRule = {
      _id: formVars.id,
      name: formVars.name,
      assignedTo: formVars.assignedTo as AssignmentsAssignTo, // TODO. Check this partial / Validate
      assignedToFallback: formVars.assignedToFallback as AssignmentsAssignTo, // TODO. Check this partial / Validate
      when: {
        criteria: formVars.exitCriteria,
      },
      followUpActions:
        formVars.followUpActions && formVars.followUpActions.length
          ? [
              {
                _id: uniqueId(),
                _name: '',
                actions: formVars.followUpActions.map(
                  (followUpAction) => followUpAction.action as AutomationAction,
                ),
              },
            ]
          : undefined, // TODO. Check this partial / Validate
    };

    const isValid =
      hasOnlyOneCriteriaAndItIsEmpty(updatedAssignmentRule) || validateAllAndReturnIsValid();

    if (isValid) {
      const sanitizedRule = sanitizeAssignmentRule(updatedAssignmentRule);
      onCreateEdit(sanitizedRule);
    }
  }, [formVars, validateAllAndReturnIsValid, onCreateEdit]);

  const clearAction = (actionIndx: number) => {
    if (formVars.followUpActions.length > 1) {
      const updatedFollowUpActions = [...formVars.followUpActions];
      updatedFollowUpActions.splice(actionIndx, 1);
      setFormVars((prev) => ({ ...prev, followUpActions: updatedFollowUpActions }));
    } else {
      setFormVars((prev) => ({ ...prev, followUpActions: [{ action: {} }] }));
    }
  };
  const addAction = () => {
    const updatedFollowUpActions = [...formVars.followUpActions, { action: {} }];
    setFormVars((prev) => ({ ...prev, followUpActions: updatedFollowUpActions }));
  };

  const { getObjectFunnelsAndRecordTypes } = useCrmOrgsApiFacade();
  const setRecordTypePerObject = useCallback(
    async (action: any) => {
      const objectName =
        (action as UpdateOrCreateAutomationAction)?.actionParams?.objectTypeName?.objectType ?? '';
      try {
        const result = await getObjectFunnelsAndRecordTypes({
          crmOrgId,
          objectName,
        });
        setRecordTypes(result?.recordTypes || []);
      } catch (error) {
        telemetry.captureError(error);
        setRecordTypes([]);
      }
    },
    [crmOrgId, getObjectFunnelsAndRecordTypes],
  );

  const hasRecordTypes = (_action: Partial<UpdateOrCreateAutomationAction>) => {
    return (
      _action.actionParams?.objectTypeName?.recordType ||
      (recordTypesList && recordTypesList.length > 1)
    );
  };

  const closeOrConfirmClose = async () => {
    if (!isPristine) {
      const isConfirmed = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);
      if (!isConfirmed) {
        return;
      }
    }
    onCancel();
  };
  /* Show Fallback */
  /* Only shows if the group has a limit */
  const { get_assignmentGroups } = useAssignmentGroupsApiFacade();
  const [showFallback, setShowFallback] = useState(false);

  const updateShowFallback = useCallback(async () => {
    const assignedTo = formVars.assignedTo;
    switch (assignedTo.type) {
      case AssignmentToType.Team: {
        const { groups } = await get_assignmentGroups();
        // Check if any weighted group has a limit (new structure)
        if (assignedTo.weightedGroups && assignedTo.weightedGroups.length > 0) {
          const hasAnyLimit = assignedTo.weightedGroups.some((wg) => {
            const group = groups.find((g) => g.id === wg.groupId);
            return group ? Boolean(group.groupLimit) : false;
          });
          setShowFallback(hasAnyLimit);
        } else {
          // Old structure: single id
          const group = groups.find((group) => group.id === assignedTo.id);
          setShowFallback(group ? Boolean(group.groupLimit) : false);
        }
        break;
      }
      case AssignmentToType.User:
      case AssignmentToType.Field:
        setShowFallback(true);
        break;
      default:
        setShowFallback(false);
    }
  }, [formVars.assignedTo, get_assignmentGroups]);

  useEffect(() => {
    updateShowFallback();
  }, [updateShowFallback]);
  /* End Show Fallback */

  const allUsedOwnershipFieldIds = useMemo(() => {
    const fields: string[] = [];
    const field = getMembershipFieldIdFromAssignTo(formVars.assignedTo);
    if (field) fields.push(field);

    formVars.followUpActions
      .filter(
        (followUpAction) => followUpAction.action.actionType === AutomationActionType.Assignment,
      )
      .forEach((followUpAction) => {
        const action = followUpAction.action;
        if (action.actionType === AutomationActionType.Assignment) {
          const field = getMembershipFieldIdFromAssignTo(action.actionParams?.assignedTo);
          if (field) fields.push(field);
        }
      });
    return fields;
  }, [formVars.assignedTo, formVars.followUpActions]);

  return (
    <CreateEditDialog
      open
      onClose={closeOrConfirmClose}
      HeaderProps={{
        hideBackButton: true,
        showCancelButton: true,
        buttonContent: editMode ? 'Save Changes' : 'Create Rule',
        buttonStartIcon: 'Automations',
        onCancelClick: closeOrConfirmClose,
        onChange: (name: string) => {
          setFormVars({
            ...formVars,
            name,
          });
        },
        onButtonClick: () => {
          applyCurrentAssignment();
        },
        placeholder: 'Add assignment name',
        value: formVars.name,
        buttonDisabled: !isAssignmentsFormValid || !isAllowedBtn,
        editDisabled: !isAllowedBtn,
        noCreateEditButtons: true,
      }}
      actionsSection={
        <Button
          size="medium"
          onClick={applyCurrentAssignment}
          disabled={!isAssignmentsFormValid || !isAllowedBtn}
          startIconName={'Automations'}
        >
          {editMode ? 'Save Changes' : 'Create Rule'}
        </Button>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '36px', paddingBottom: 3 }}>
        <SectionBox>
          <Box>
            <CustomChip
              label="WHEN"
              sx={{
                backgroundColor: colors.gum[500],
                color: colors.white,
              }}
            />
          </Box>
          <Box>
            <Box
              sx={{
                backgroundColor: colors.grey[100],
                paddingTop: '22px',
                borderRadius: '16px',
                '.rbHeader': {
                  padding: 0,
                  margin: 0,
                },
                '.rule-builder-logic-preview': {
                  marginRight: '22px',
                },
              }}
            >
              <CustomHeader customHeader="Those conditions are met" />
              <SweepFieldsRuleBuilder
                ref={ruleBuilderRef}
                readOnly={!isAllowedBtn}
                sweepCondition={formVars.exitCriteria}
                onChange={function (newSweepCondition: SweepCondition) {
                  setFormVars({
                    ...formVars,
                    exitCriteria: {
                      ...formVars.exitCriteria,
                      ...newSweepCondition,
                    },
                  });
                }}
                crmOrgId={crmOrgId}
                objectType={objectType}
              />
            </Box>
          </Box>
        </SectionBox>
        <SectionBox>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <CustomChip
              label="ASSIGN TO"
              sx={{
                backgroundColor: colors.blush[300],
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ActionsMenu
                actions={[
                  {
                    label: 'Clear',
                    value: 'Clear',
                    disabled: !isAllowedBtn,
                  },
                  {
                    label: 'Add Another Action',
                    value: 'Add',
                    disabled: !isAllowedBtn,
                  },
                ]}
                onClick={(action) => {
                  switch (action.value) {
                    case 'Clear':
                      setFormVars({
                        ...formVars,
                        assignedTo: {},
                      });
                      break;
                    case 'Add': {
                      addAction();
                      break;
                    }
                  }
                }}
              />
            </Box>
          </Box>
          <AssignToSection
            crmOrgId={crmOrgId}
            objectType={objectType}
            assignedTo={formVars.assignedTo}
            assignedToFallback={formVars.assignedToFallback}
            onChange={useCallback(
              (props) => {
                setFormVars({ ...formVars, ...props });
              },
              [formVars, setFormVars],
            )}
            allUsedOwnershipFieldIds={allUsedOwnershipFieldIds}
            isAllowedBtn={isAllowedBtn}
            showFallback={showFallback}
          />
        </SectionBox>
        <SectionBox>
          {formVars.followUpActions.map((followUpAction, actionIndex) => {
            const { action } = followUpAction;

            return (
              <Box key={followUpAction.action._id ?? uniqueId()}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <CustomChip
                    label="AND THEN"
                    sx={{
                      backgroundColor: colors.lemon[500],
                      color: colors.black,
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ActionsMenu
                      actions={[
                        {
                          label: 'Clear',
                          value: 'Clear',
                          disabled: !isAllowedBtn,
                        },
                        {
                          label: 'Add Another Action',
                          value: 'Add',
                          disabled:
                            !isAllowedBtn || followUpAction.actionSelectOption === 'DO_NOT_NOTIFY',
                        },
                      ]}
                      onClick={(action) => {
                        switch (action.value) {
                          case 'Clear':
                            clearAction(actionIndex);
                            break;
                          case 'Add': {
                            addAction();
                            break;
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '16px' }}>
                  <Box>
                    <Select
                      disabled={!isAllowedBtn}
                      placeholder={'Do this'}
                      value={followUpAction.actionSelectOption ?? ''}
                      onChange={(event) => {
                        const updatedAction = cloneDeep(followUpAction);
                        updatedAction.actionSelectOption = event.target
                          .value as FollowUpActionSelectOptions;
                        switch (event.target.value) {
                          case AutomationActionType.SendEmail: {
                            updatedAction.action._id = followUpAction.action._id ?? uniqueId();
                            updatedAction.action.actionType = AutomationActionType.SendEmail;
                            updatedAction.action = {
                              ...updatedAction.action,
                              actionParams: undefined,
                            };
                            break;
                          }
                          case AutomationActionType.Slack: {
                            updatedAction.action._id = followUpAction.action._id ?? uniqueId();
                            updatedAction.action.actionType = AutomationActionType.Slack;
                            updatedAction.action = {
                              ...updatedAction.action,
                              actionParams: undefined,
                            };
                            break;
                          }
                          case AutomationActionType.UpdateRecords: {
                            updatedAction.action._id = followUpAction.action._id ?? uniqueId();
                            updatedAction.action.actionType = AutomationActionType.UpdateRecords;
                            updatedAction.action = {
                              ...updatedAction.action,
                              actionType: AutomationActionType.UpdateRecords,
                              actionParams: { thisRecord: true },
                            };
                            break;
                          }
                          case AutomationActionType.SlackAddMember: {
                            updatedAction.action._id = followUpAction.action._id ?? uniqueId();
                            updatedAction.action.actionType = AutomationActionType.SlackAddMember;
                            updatedAction.action = {
                              ...updatedAction.action,
                              actionParams: undefined,
                            };
                            break;
                          }
                          case AutomationActionType.CreateRecord: {
                            updatedAction.action._id = followUpAction.action._id ?? uniqueId();
                            updatedAction.action.actionType = AutomationActionType.CreateRecord;
                            updatedAction.action = {
                              ...updatedAction.action,
                              actionParams: undefined,
                            };
                            break;
                          }
                          case AutomationActionType.Assignment: {
                            updatedAction.action._id = followUpAction.action._id ?? uniqueId();
                            updatedAction.action.actionType = AutomationActionType.Assignment;
                            updatedAction.action = {
                              ...updatedAction.action,
                              actionParams: undefined,
                            };
                            break;
                          }
                        }
                        const updatedFollowUpActions = [...formVars.followUpActions];
                        updatedFollowUpActions[actionIndex] = updatedAction;

                        setFormVars((prev) => ({
                          ...prev,
                          followUpActions: updatedFollowUpActions,
                        }));
                      }}
                    >
                      <MenuItem value={''} disabled hidden>
                        Disabled
                      </MenuItem>
                      <MenuItem value={AutomationActionType.SendEmail}>Send Email</MenuItem>
                      <MenuItem value={AutomationActionType.Slack}>Notify to Slack</MenuItem>
                      <MenuItem value={AutomationActionType.UpdateRecords}>Update record</MenuItem>
                      <MenuItem value={AutomationActionType.CreateRecord}>Create record</MenuItem>
                      {slackStatus === SlackConnectionStatus.Connected && (
                        <MenuItem value={AutomationActionType.SlackAddMember}>
                          Add members to deal room
                        </MenuItem>
                      )}
                      <MenuItem value={AutomationActionType.Assignment}>Assign To</MenuItem>
                    </Select>
                  </Box>

                  {followUpAction.action?.actionType === AutomationActionType.CreateRecord && (
                    <Box>
                      <FormControl sx={{ minWidth: '160px', marginRight: '8px', width: '100%' }}>
                        <ActionObjectTypesAutocomplete
                          _action={followUpAction.action as UpdateOrCreateAutomationAction}
                          crmOrgId={crmOrgId}
                          disabled={!isAllowedBtn}
                          onChange={(automationAction) => {
                            const updateFollowUpActions = [...formVars.followUpActions];
                            updateFollowUpActions[actionIndex].action = automationAction;
                            setFormVars((prev) => ({
                              ...prev,
                              followUpActions: updateFollowUpActions,
                            }));
                            setRecordTypePerObject(followUpAction.action);
                          }}
                        />
                      </FormControl>
                    </Box>
                  )}
                  {followUpAction.action?.actionType === AutomationActionType.CreateRecord &&
                    followUpAction.action?.actionParams?.objectTypeName &&
                    hasRecordTypes(followUpAction.action) && (
                      <Box>
                        <SweepSelect
                          FormControlProps={{
                            sx: { minWidth: '140px' },
                          }}
                          SelectProps={{
                            disabled: !isAllowedBtn,
                            placeholder: 'Choose Record Type',
                            value:
                              (followUpAction.action as UpdateOrCreateAutomationAction).actionParams
                                ?.objectTypeName?.recordType?.name ?? '',
                            renderValue: (val: any) => {
                              if (!recordTypesList) {
                                setRecordTypePerObject(followUpAction.action);
                              }
                              const recordType = recordTypesList?.find(
                                (type) => type?.name === val,
                              );
                              return recordType?.label ?? val ?? '';
                            },
                            onChange: (event: SelectChangeEvent<unknown>) => {
                              const val = event.target.value;
                              const getRecordTypeByName = recordTypesList?.find(
                                (type) => type?.name === val,
                              );
                              const newObjectTypeName = {
                                ...(followUpAction.action as UpdateOrCreateAutomationAction)
                                  .actionParams?.objectTypeName,
                                recordType: getRecordTypeByName,
                              };
                              const automationAction = {
                                ...(followUpAction.action as UpdateOrCreateAutomationAction),
                                actionParams: {
                                  ...(followUpAction.action as UpdateOrCreateAutomationAction)
                                    ?.actionParams,
                                  objectTypeName: newObjectTypeName as ObjectTypeName,
                                },
                              };

                              const updateFollowUpActions = [...formVars.followUpActions];
                              updateFollowUpActions[actionIndex].action = automationAction;
                              setFormVars((prev) => ({
                                ...prev,
                                followUpActions: updateFollowUpActions,
                              }));
                            },
                          }}
                        >
                          {recordTypesList?.map((type) => (
                            <MenuItem key={type.name} value={type.name}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </SweepSelect>
                      </Box>
                    )}
                </Box>
                {followUpAction.action.actionType === AutomationActionType.SendEmail && (
                  <AutomationNotifyEmail
                    action={followUpAction.action as EmailAutomationAction}
                    crmOrgId={crmOrgId}
                    onChange={(automationAction) => {
                      const updateFollowUpActions = [...formVars.followUpActions];
                      updateFollowUpActions[actionIndex].action = automationAction;
                      setFormVars((prev) => ({
                        ...prev,
                        followUpActions: updateFollowUpActions,
                      }));
                    }}
                    objectName={objectType}
                  />
                )}
                {followUpAction.action.actionType === AutomationActionType.Slack && (
                  <SlackNotificationAction
                    readonly={false}
                    action={formVars.followUpActions[actionIndex].action as SlackAutomationAction}
                    crmOrgId={crmOrgId}
                    onChange={(automationAction) => {
                      const updateFollowUpActions = [...formVars.followUpActions];
                      updateFollowUpActions[actionIndex].action = automationAction;
                      setFormVars((prev) => ({
                        ...prev,
                        followUpActions: updateFollowUpActions,
                      }));
                    }}
                    objectName={objectType}
                  />
                )}
                {followUpAction.action.actionType === AutomationActionType.UpdateRecords && (
                  <Box className="automationFieldLineWrapper" sx={{ width: '100%', mt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <AutomationActionRows
                        initialObjectName={objectType}
                        disableLookupItems={false}
                        hideRecordTypeIdField={true}
                        objectName={objectType}
                        cmOrgId={crmOrgId}
                        onChange={(fields) => {
                          const updateFollowUpActions = cloneDeep(formVars.followUpActions);
                          (
                            updateFollowUpActions[actionIndex]
                              .action as UpdateOrCreateAutomationAction
                          ).actionParams._fields = fields;
                          setFormVars((prev) => ({
                            ...prev,
                            followUpActions: updateFollowUpActions,
                          }));
                        }}
                        fields={
                          (
                            formVars.followUpActions[actionIndex]
                              .action as UpdateOrCreateAutomationAction
                          ).actionParams?._fields || []
                        }
                      />
                    </Box>
                  </Box>
                )}
                {followUpAction.action.actionType === AutomationActionType.SlackAddMember && (
                  <SlackAddMemberAction
                    action={
                      formVars.followUpActions[actionIndex].action as SlackAddMemberAutomationAction
                    }
                    crmOrgId={crmOrgId}
                    onChange={(automationAction) => {
                      const updateFollowUpActions = [...formVars.followUpActions];
                      updateFollowUpActions[actionIndex].action = automationAction;
                      setFormVars((prev) => ({
                        ...prev,
                        followUpActions: updateFollowUpActions,
                      }));
                    }}
                    objectName={objectType}
                  />
                )}
                {action.actionType === AutomationActionType.Assignment && (
                  <Box mt={2}>
                    <AssignToSection
                      crmOrgId={crmOrgId}
                      objectType={objectType}
                      assignedTo={action.actionParams?.assignedTo}
                      assignedToFallback={action.actionParams?.assignedToFallback}
                      onChange={({ assignedTo, assignedToFallback }) => {
                        const updateFollowUpActions = [...formVars.followUpActions];
                        updateFollowUpActions[actionIndex].action = {
                          ...followUpAction.action,
                          actionType: AutomationActionType.Assignment,
                          actionParams: {
                            assignedTo,
                            assignedToFallback,
                          },
                        };
                        setFormVars((prev) => ({
                          ...prev,
                          followUpActions: updateFollowUpActions,
                        }));
                      }}
                      allUsedOwnershipFieldIds={allUsedOwnershipFieldIds}
                      isAllowedBtn={isAllowedBtn}
                      showFallback={showFallback}
                    />
                  </Box>
                )}

                {followUpAction.action.actionType === AutomationActionType.CreateRecord &&
                  followUpAction.action.actionParams?.objectTypeName && (
                    <Box sx={{ display: 'flex', mt: 1 }}>
                      <Box
                        sx={{
                          marginRight: '12px',
                          paddingTop: '10px',
                          flex: '0 0 auto',
                          maxWidth: '160px',
                          width: '160px',
                        }}
                      >
                        <Typography variant="body">
                          <>
                            Set the new{' '}
                            <b>{followUpAction.action.actionParams?.objectTypeName?.label}</b>
                            ’s field value(s):
                          </>
                        </Typography>
                      </Box>
                      <Box className="automationFieldLineWrapper" sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <AutomationActionRows
                            showCreatableFields={true}
                            disableLookupItems={true}
                            hideRecordTypeIdField={true}
                            readonly={!isAllowedBtn}
                            objectName={
                              followUpAction.action?.actionParams?.objectTypeName?.objectType
                            }
                            cmOrgId={crmOrgId}
                            initialObjectName={objectType}
                            onChange={(fields) => {
                              const updateFollowUpActions = cloneDeep(formVars.followUpActions);
                              (
                                updateFollowUpActions[actionIndex]
                                  .action as UpdateOrCreateAutomationAction
                              ).actionParams._fields = fields;
                              setFormVars((prev) => ({
                                ...prev,
                                followUpActions: updateFollowUpActions,
                              }));
                            }}
                            fields={
                              (
                                formVars.followUpActions[actionIndex]
                                  .action as UpdateOrCreateAutomationAction
                              ).actionParams?._fields || []
                            }
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
              </Box>
            );
          })}
        </SectionBox>
      </Box>
    </CreateEditDialog>
  );
};
const AssignmentRuleDialogWithProviders = (props: AssignmentsDialogProps) => (
  <MultipleRuleBuildersProvider>
    <AssignmentRuleDialog {...props} />
  </MultipleRuleBuildersProvider>
);

export { AssignmentRuleDialogWithProviders as AssignmentRuleDialog };
